import type { CanonicalSessionProjectionV1 } from '../core/session/session-projection.js'
import type { TranscriptItem } from '../types/visual.js'

export const LIVE_SCREEN_INTENT_REGEX =
  /\b(screen|share|screenshare|screen share|skjerm(?:deling)?|logs|vercel|dashboard|deployment|console|terminal|browser|page)\b/i
/** Explicit camera requests only. Metaphorical room/face/environment must not match. */
export const LIVE_WEBCAM_INTENT_REGEX =
  /\b(webcam|camera|see me|look at me)\b/i

const VISUAL_RECALL_REGEX =
  /\b(?:what did you see|what have you seen|what you have seen|tell me what you saw|describe what you saw|what you saw(?: earlier)?|summar(?:ize|ise|y|riz)(?: what| everything)? you saw|what was on|what was on my screen|what was on the webcam|what did you see with your vision|and what did you see|did you see anything|did you see me|what (?:was|am) i holding|what did i show you|what did you show me|oppsummer hva jeg viste)\b/i

const NORWEGIAN_VISUAL_RECALL_REGEX =
  /\b(?:hva så du|hva så du på(?: kamera| skjerm(?:deling)?)?|hva holdt jeg(?: i hånden)?|hva viste jeg(?: deg)?|oppsummer hva jeg viste)\b/i

const RECALL_COP_OUT_REGEX =
  /\b(?:I recall that setup|don't have an active feed|do not have an active feed|no active feed|verified current webcam frame|should not describe what I see|can't describe what I see|cannot describe what I see|I don't have a current webcam|I do not have a verified current webcam)\b/i

const VISUAL_DENIAL_REGEX =
  /\b(?:can't see|cannot see|don't see|do not see|can't view|cannot view|don't have an active feed|no active feed|no webcam feed|can't access|cannot access|cannot view past|can't view past|please use the screen share|screen share button|I cannot access or view past video)\b/i

const CONCRETE_VISUAL_NOUN_REGEX =
  /\b(?:dashboard|deployment|webcam|camera|plant|room|screen|vercel|console|table|fish tank|sunglasses|artwork|logs|timeline|shirt|background|monitor|window|desk|share|remote|gesture|hand|face|person|chair|sofa|couch|wall|browser|page|smartphone|phone|holding|google|earbuds?|earbud case)\b/i

const LIVE_VISUAL_DESCRIPTION_REGEX =
  /\b(?:I see|I can see|I’m seeing|I'm seeing|visible|looks like|appears to be|in front of|behind you|on your screen|on the screen|on camera|on webcam|you(?:'re| are) (?:holding|wearing)|holding a)\b/i

const SCREEN_ANALYSIS_HINT_REGEX =
  /\b(?:screen|dashboard|deployment|vercel|console|terminal|browser|page|logs|window|tab|ui|interface)\b/i

const WEBCAM_ANALYSIS_HINT_REGEX =
  /\b(?:webcam|camera|you|your gesture|gesture|remote|room|face|person|behind you|in front of|background|desk|plant|shirt|sunglasses|artwork|wall|chair|sofa|couch|earbuds?|earbud case|wearing)\b/i

export type VisualModality = 'webcam' | 'screen'

export interface VisualModalityState {
  webcam: boolean
  screen: boolean
}

/** Visitor said they have not shared camera/screen — do not paint a no-frame fake. */
export function deniesLiveVisualShare(text: string): boolean {
  const normalized = text.trim().toLowerCase()
  if (!normalized) return false
  return (
    /\b(?:haven'?t|have not|did not|didn't|not)\s+(?:shared|sharing|started|turned on|enabled)\b/.test(
      normalized
    ) ||
    /\b(?:ikke\s+delt|har ikke delt|uten (?:kamera|webcam|skjerm)|ingen (?:skjerm|kamera|webcam))\b/.test(
      normalized
    ) ||
    /\b(?:i(?:'| a)?m not sharing|not sharing (?:my )?(?:screen|webcam|camera|skjerm))\b/.test(
      normalized
    )
  )
}

export interface CanonicalVisualObservation {
  evidenceId: string
  modality: VisualModality
  analysis: string
  at: string
  active: boolean
}

export function isVisualRecallQuestion(message: string): boolean {
  const normalized = message.trim()
  if (!normalized) return false
  if (VISUAL_RECALL_REGEX.test(normalized)) return true
  if (NORWEGIAN_VISUAL_RECALL_REGEX.test(normalized)) return true

  const lower = normalized.toLowerCase()
  const hasVisualModality =
    /\b(?:webcam|screenshare|screen share|screen|kamera|skjerm(?:deling)?)\b/i.test(lower)
  if (!hasVisualModality) return false
  if (/\bwhat i shared\b/i.test(lower)) return true
  if (/\b(?:summariz|summaris|summri|summriz)(?:e|ing|ze)?\b/i.test(lower)) return true
  return /\bfull list\b/i.test(lower)
}

export function isVisualDenialAnalysis(text: string): boolean {
  const normalized = text.trim()
  if (!normalized) return true
  if (VISUAL_DENIAL_REGEX.test(normalized)) return true
  return (
    /^no,?\s/i.test(normalized) &&
    /\b(?:see|screen|webcam|camera|share|feed)\b/i.test(normalized)
  )
}

export function isRecallCopOutAnalysis(text: string): boolean {
  const normalized = text.trim()
  if (!normalized) return true
  if (RECALL_COP_OUT_REGEX.test(normalized)) return true
  if (/\bI recall\b/i.test(normalized) && !CONCRETE_VISUAL_NOUN_REGEX.test(normalized)) {
    return true
  }
  return (
    /\bfrom earlier\b/i.test(normalized) &&
    normalized.length < 180 &&
    !CONCRETE_VISUAL_NOUN_REGEX.test(normalized)
  )
}

export function isConcreteLiveVisualAnalysis(text: string): boolean {
  const normalized = text.trim()
  if (!normalized) return false
  if (isRecallCopOutAnalysis(normalized) || isVisualDenialAnalysis(normalized)) return false
  if (/\bI see your point\b/i.test(normalized)) return false
  return (
    CONCRETE_VISUAL_NOUN_REGEX.test(normalized) &&
    LIVE_VISUAL_DESCRIPTION_REGEX.test(normalized)
  )
}

function transcriptItemText(item: TranscriptItem | undefined): string {
  return typeof item?.text === 'string' ? item.text.trim() : ''
}

export function isLiveVisualUserPrompt(text: string): boolean {
  const normalized = text.trim()
  if (!normalized || isVisualRecallQuestion(normalized)) return false
  if (deniesLiveVisualShare(normalized)) return false

  const asksForVision = /\b(?:see|show|describe|look|what|read|summarize|tell)\b/i.test(
    normalized
  )
  if (LIVE_SCREEN_INTENT_REGEX.test(normalized) && asksForVision) return true
  if (LIVE_WEBCAM_INTENT_REGEX.test(normalized) && asksForVision) return true

  const lower = normalized.toLowerCase()
  return (
    lower.includes('can you see me') ||
    lower.includes('do you see me') ||
    lower.includes('what do you see')
  )
}

export function resolveLastVisualUserPrompt(transcript: TranscriptItem[]): string | null {
  for (let index = transcript.length - 1; index >= 0; index--) {
    const item = transcript[index]
    if (item?.role === 'user' && item.text?.trim()) return item.text.trim()
  }
  return null
}

export function inferVisualModalityFromPrompt(
  userText: string,
  modalities: VisualModalityState
): VisualModality | null {
  const asksScreen = LIVE_SCREEN_INTENT_REGEX.test(userText)
  const asksWebcam = LIVE_WEBCAM_INTENT_REGEX.test(userText)

  if (asksScreen && !asksWebcam) return 'screen'
  if (asksWebcam && !asksScreen) return 'webcam'
  if (asksScreen && asksWebcam) {
    if (modalities.screen && !modalities.webcam) return 'screen'
    if (modalities.webcam && !modalities.screen) return 'webcam'
    return 'screen'
  }

  if (modalities.screen && !modalities.webcam) return 'screen'
  if (modalities.webcam && !modalities.screen) return 'webcam'
  if (modalities.screen || modalities.webcam) return modalities.screen ? 'screen' : 'webcam'
  return null
}

export function inferVisualModalityFromAnalysis(
  analysis: string,
  modalities: VisualModalityState
): VisualModality | null {
  const mentionsScreen = SCREEN_ANALYSIS_HINT_REGEX.test(analysis)
  const mentionsWebcam = WEBCAM_ANALYSIS_HINT_REGEX.test(analysis)

  if (mentionsScreen && !mentionsWebcam) return 'screen'
  if (mentionsWebcam && !mentionsScreen) return 'webcam'
  if (mentionsScreen && mentionsWebcam) {
    if (modalities.screen && !modalities.webcam) return 'screen'
    if (modalities.webcam && !modalities.screen) return 'webcam'
  }
  if (modalities.webcam && !modalities.screen) return 'webcam'
  if (modalities.screen && !modalities.webcam) return 'screen'
  return null
}

/** Persistable webcam/screen observation from a live assistant description. */
export function inferVisualObservationFromAssistantText(
  text: string,
): { modality: VisualModality; analysis: string } | null {
  const analysis = text.trim()
  if (!isConcreteLiveVisualAnalysis(analysis)) return null
  const modality =
    inferVisualModalityFromAnalysis(analysis, { webcam: true, screen: true }) ??
    (SCREEN_ANALYSIS_HINT_REGEX.test(analysis) ? 'screen' : 'webcam')
  return { modality, analysis }
}

export function extractLiveVisualPairs(
  transcript: TranscriptItem[],
  modalities: VisualModalityState,
  maxPairs = 5
): Array<{ userText: string; modelText: string; modality: VisualModality }> {
  const pairs: Array<{ userText: string; modelText: string; modality: VisualModality }> = []

  for (let index = 0; index < transcript.length - 1; index++) {
    const item = transcript[index]
    const next = transcript[index + 1]
    if (!item || !next || item.role !== 'user' || next.role !== 'model') continue
    if (!item.isFinal || !next.isFinal) continue

    const userText = transcriptItemText(item)
    const modelText = transcriptItemText(next)
    if (!userText || !modelText || isVisualRecallQuestion(userText)) continue
    if (!isConcreteLiveVisualAnalysis(modelText)) continue

    const modality = isLiveVisualUserPrompt(userText)
      ? inferVisualModalityFromPrompt(userText, modalities)
      : inferVisualModalityFromAnalysis(modelText, modalities)
    if (modality) pairs.push({ userText, modelText, modality })
  }

  const bestByModality = new Map<VisualModality, (typeof pairs)[number]>()
  for (const pair of pairs) {
    const existing = bestByModality.get(pair.modality)
    if (!existing || pair.modelText.length > existing.modelText.length) {
      bestByModality.set(pair.modality, pair)
    }
  }
  return Array.from(bestByModality.values()).slice(-maxPairs)
}

export function collectTranscriptVisualCandidates(
  transcript: TranscriptItem[],
  modalities: VisualModalityState,
  maxPairs = 5
): Array<{ modality: VisualModality; analysis: string }> {
  return extractLiveVisualPairs(transcript, modalities, maxPairs).map(pair => ({
    modality: pair.modality,
    analysis: pair.modelText.trim(),
  }))
}

export function getCanonicalVisualObservations(
  projection: CanonicalSessionProjectionV1 | undefined
): CanonicalVisualObservation[] {
  if (!projection) return []

  return projection.evidence.flatMap(evidence => {
    if (
      evidence.status !== 'verified' ||
      (evidence.kind !== 'screen' && evidence.kind !== 'webcam') ||
      !evidence.summary.trim()
    ) {
      return []
    }

    return [
      {
        evidenceId: evidence.evidenceId,
        modality: evidence.kind,
        analysis: evidence.summary.trim(),
        at: evidence.capturedAt,
        active: projection.modalities[evidence.kind].active,
      },
    ]
  })
}

export function getCanonicalVisualModalityStatus(
  projection: CanonicalSessionProjectionV1 | undefined
): Record<VisualModality, { active: boolean; lastEventAt: string | null }> {
  return {
    webcam: {
      active: projection?.modalities.webcam.active ?? false,
      lastEventAt: projection?.modalities.webcam.lastEventAt ?? null,
    },
    screen: {
      active: projection?.modalities.screen.active ?? false,
      lastEventAt: projection?.modalities.screen.lastEventAt ?? null,
    },
  }
}

export function buildPriorVisualRecallBlock(
  projection: CanonicalSessionProjectionV1 | undefined
): string {
  const observations = getCanonicalVisualObservations(projection)
  if (observations.length === 0) return ''

  const modalities = getCanonicalVisualModalityStatus(projection)
  const prefix =
    !modalities.webcam.active || !modalities.screen.active
      ? 'No current live webcam/screen frame is active. Prior verified observations from this session:'
      : 'Prior verified visual observations from this session (use for recall questions):'
  const lines = observations.map(observation => {
    const label = observation.modality === 'screen' ? 'Screen share' : 'Webcam'
    return `- ${label}: ${observation.analysis}`
  })

  return `${prefix}\n${lines.join('\n')}\n\nWhen the user asks what you saw earlier, answer from these observations. Say clearly when no current frame is active.`
}
